import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
} from '@mui/material';
import React, { ReactNode } from 'react';
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { generateUniqueKey } from '../../../helpers/app';

export type DropDownOption = {
  label: ReactNode;
  value: string | number;
  disabled?: boolean;
};

interface DropdownProps {
  disabled?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  initialValue?: string;
  name?: string;
  options: DropDownOption[];
  required?: boolean;
  withError?: boolean;
  onChange?: (event: SelectChangeEvent<string>, child?: ReactNode) => void;
  field?: ControllerRenderProps<any, any>;
  sx?: SxProps<Theme>;
}

const Dropdown: React.FC<DropdownProps> = ({
  name,
  initialValue = '',
  required = false,
  options,
  onChange,
  disabled = false,
  fullWidth = false,
  withError = false,
  helperText,
  field,
  sx,
  ...props
}) => {
  const { control } = useFormContext();

  // If field is provided, use it directly (controlled by parent Controller)
  if (field) {
    return (
      <FormControl fullWidth={fullWidth} disabled={disabled} error={withError}>
        <Select
          {...field}
          onChange={(event, child) => {
            field.onChange(event.target.value);
            onChange?.(event, child);
          }}
          {...props}
          displayEmpty
          sx={{ backgroundColor: '#fff', ...sx }}
        >
          <MenuItem key={generateUniqueKey()} value="" disabled>
            {`Select from options`}
          </MenuItem>
          {options.map((option: DropDownOption) => (
            <MenuItem
              key={generateUniqueKey()}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{helperText ?? ''}</FormHelperText>
      </FormControl>
    );
  }

  // Otherwise, use internal Controller
  if (!name) {
    console.warn('Dropdown: Either name or field prop must be provided');
    return null;
  }

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled} error={withError}>
      <Controller
        name={name}
        control={control}
        defaultValue={initialValue}
        rules={{ required }}
        render={({ field }) => (
          <Select
            {...field}
            onChange={(event, child) => {
              field.onChange(event.target.value);
              onChange?.(event, child);
            }}
            {...props}
            displayEmpty
          >
            <MenuItem key={generateUniqueKey()} value="" disabled>
              {`Select from options`}
            </MenuItem>
            {options.map((option: DropDownOption) => (
              <MenuItem
                key={generateUniqueKey()}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
      />
      <FormHelperText>{helperText ?? ''}</FormHelperText>
    </FormControl>
  );
};

export default Dropdown;
